type AsyncFunction<TArgs extends readonly unknown[], TReturn> = (...args: TArgs) => Promise<TReturn>;

type Options<TArgs extends readonly unknown[]> = {
	cacheKey?: (...args: TArgs) => string;
};

type PMemoPublicApi = {
	clearCache: () => void;
};

const DEFAULT_CACHE_KEY = <TArgs extends readonly unknown[]>(...args: TArgs) => String(args[0]);

export function promiseMemo<TArgs extends readonly unknown[], TReturn>(
	callback: AsyncFunction<TArgs, TReturn>,
	options: Options<TArgs> = {}
): AsyncFunction<TArgs, TReturn> & PMemoPublicApi {
	const { cacheKey = DEFAULT_CACHE_KEY } = options;
	const cache: Map<string, Promise<TReturn>> = new Map();

	const promiseMemoWrapper = async (...args: TArgs): Promise<TReturn> => {
		const key = cacheKey(...args);

		if (!cache.has(key)) {
			cache.set(key, callback(...args));
		}

		return cache.get(key);
	};

	promiseMemoWrapper.clearCache = () => {
		cache.clear();
	};

	return promiseMemoWrapper;
}

export function promiseMemoOne<TArgs extends readonly unknown[], TReturn>(
	callback: AsyncFunction<TArgs, TReturn>,
	options: Options<TArgs> = {}
): AsyncFunction<TArgs, TReturn> & PMemoPublicApi {
	const { cacheKey = () => '_key_' } = options;

	let cache: Promise<TReturn> | null = null;
	let prevKey: string;

	const promiseMemoWrapper = async (...args: TArgs): Promise<TReturn> => {
		const currentKey = cacheKey(...args);

		if (currentKey !== prevKey || cache === null) {
			cache = callback(...args);
			prevKey = currentKey;
		}

		return cache;
	};

	promiseMemoWrapper.clearCache = () => {
		cache = null;
	};

	return promiseMemoWrapper;
}
